import { StaffStatus } from "../utils";

const ASSIGNMENT_STORAGE_PREFIX = "resident-assignment:v1:";
const POLICY_STORAGE_PREFIX = "business-alert-policy:v1:";

export const SHIFT_WINDOWS = {
  AM: { label: "Morning (AM)", startMinute: 6 * 60, endMinute: 14 * 60 - 1 },
  PM: { label: "Afternoon/Evening (PM)", startMinute: 14 * 60, endMinute: 22 * 60 - 1 },
  NOC: { label: "Night (NOC)", startMinute: 22 * 60, endMinute: 6 * 60 - 1 }, // overnight
};

export function getShiftCodeForDate(now = new Date()) {
  const minutes = now.getHours() * 60 + now.getMinutes();
  if (minutes >= SHIFT_WINDOWS.AM.startMinute && minutes <= SHIFT_WINDOWS.AM.endMinute) return "AM";
  if (minutes >= SHIFT_WINDOWS.PM.startMinute && minutes <= SHIFT_WINDOWS.PM.endMinute) return "PM";
  return "NOC";
}

function assignmentStorageKey(businessId) {
  return `${ASSIGNMENT_STORAGE_PREFIX}${String(businessId || "default")}`;
}

function policyStorageKey(businessId) {
  return `${POLICY_STORAGE_PREFIX}${String(businessId || "default")}`;
}

export function loadResidentAssignmentsMap(businessId) {
  try {
    const raw = localStorage.getItem(assignmentStorageKey(businessId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function saveResidentAssignmentsMap(businessId, map) {
  localStorage.setItem(assignmentStorageKey(businessId), JSON.stringify(map || {}));
}

export function getResidentAssignment(businessId, residentId) {
  const map = loadResidentAssignmentsMap(businessId);
  return map[String(residentId)] || {
    AM: { primaryStaffId: "", backupStaffIds: [] },
    PM: { primaryStaffId: "", backupStaffIds: [] },
    NOC: { primaryStaffId: "", backupStaffIds: [] },
  };
}

export function saveResidentAssignment(businessId, residentId, assignment) {
  const map = loadResidentAssignmentsMap(businessId);
  map[String(residentId)] = assignment;
  saveResidentAssignmentsMap(businessId, map);
}

export function loadBusinessAlertPolicy(businessId) {
  try {
    const raw = localStorage.getItem(policyStorageKey(businessId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function saveBusinessAlertPolicyLocal(businessId, policy) {
  localStorage.setItem(policyStorageKey(businessId), JSON.stringify(policy || {}));
}

function parseTimeToMinute(value) {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return null;
  if (v === "all day") return 0;

  const meridianMatch = v.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
  if (meridianMatch) {
    let h = Number(meridianMatch[1]);
    const m = Number(meridianMatch[2]);
    const ap = meridianMatch[3];
    if (ap === "pm" && h < 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
    return h * 60 + m;
  }

  const hhmm = v.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hhmm) {
    const h = Number(hhmm[1]);
    const m = Number(hhmm[2]);
    if (h >= 0 && h < 24 && m >= 0 && m < 60) return h * 60 + m;
  }
  return null;
}

function isMinuteInRange(minute, start, end) {
  if (start == null || end == null) return false;
  if (start <= end) return minute >= start && minute <= end;
  return minute >= start || minute <= end;
}

function normalizeDay(day) {
  return String(day || "").trim().toLowerCase();
}

function nowDayName(now = new Date()) {
  return now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
}

export function isStaffOnDuty(staff, now = new Date()) {
  if (!staff || Number(staff?.status) !== Number(StaffStatus.ACTIVE)) return false;
  const groups = Array.isArray(staff?.workingHourGroups) ? staff.workingHourGroups : [];
  const group = groups.find((g) => g?.def) || groups[0];
  const hours = Array.isArray(group?.workingHours) ? group.workingHours : [];
  if (!hours.length) return false;

  const day = nowDayName(now);
  const currentMinute = now.getHours() * 60 + now.getMinutes();

  const todays = hours.filter(
    (h) => Boolean(h?.onJob) && normalizeDay(h?.day) === day
  );
  if (!todays.length) return false;

  return todays.some((slot) => {
    if (String(slot?.startTime || "").toLowerCase() === "all day") return true;
    const start = parseTimeToMinute(slot?.startTime);
    const end = parseTimeToMinute(slot?.endTime);
    return isMinuteInRange(currentMinute, start, end);
  });
}

function dedupeById(arr) {
  const out = [];
  const seen = new Set();
  for (const item of arr || []) {
    const id = String(item?.id || "");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(item);
  }
  return out;
}

function mapStaffById(staffs) {
  const byId = new Map();
  (staffs || []).forEach((s) => {
    if (s?.id == null) return;
    byId.set(String(s.id), s);
  });
  return byId;
}

function activeStaff(staffs) {
  return (staffs || []).filter((s) => Number(s?.status) === Number(StaffStatus.ACTIVE));
}

function byRoles(staffs, roles = []) {
  const wanted = new Set((roles || []).map((r) => String(r || "").trim().toLowerCase()).filter(Boolean));
  if (!wanted.size) return [];
  return (staffs || []).filter((s) => wanted.has(String(s?.role?.name || "").trim().toLowerCase()));
}

export function resolveAlertRecipients({
  business,
  resident,
  businessId,
  alertType,
  explicitAssigneeId = null,
  now = new Date(),
}) {
  const staffs = activeStaff(business?.businessStaffs || []);
  const byId = mapStaffById(staffs);
  const policy = loadBusinessAlertPolicy(businessId) || {};
  const normalizedAlertType = normalizeAlertType(alertType);
  const rule = policy?.rules?.[normalizedAlertType] || {};
  const routingMode = String(
    rule?.routingMode || (String(normalizedAlertType || "").toUpperCase() === "MEDICATION_LATE" ? "ASSIGNEE_FIRST" : "ROLE_BASED")
  ).toUpperCase();
  const resolved = [];
  const reasons = [];

  // 1) Explicit task assignee
  if (explicitAssigneeId != null) {
    const s = byId.get(String(explicitAssigneeId));
    if (s) {
      resolved.push(s);
      reasons.push("explicit task assignee");
      return { recipients: dedupeById(resolved), reasons };
    }
  }

  // 2) Resident shift assignment (current window)
  const shiftCode = getShiftCodeForDate(now);
  const assignment = getResidentAssignment(businessId, resident?.id);
  const shiftConfig = assignment?.[shiftCode] || {};
  const primaryId = shiftConfig?.primaryStaffId ? String(shiftConfig.primaryStaffId) : "";
  const backupIds = Array.isArray(shiftConfig?.backupStaffIds)
    ? shiftConfig.backupStaffIds.map((x) => String(x))
    : [];

  if (primaryId) {
    const primary = byId.get(primaryId);
    if (primary) {
      resolved.push(primary);
      reasons.push(`resident ${shiftCode} primary`);
    }
  }
  backupIds.forEach((id) => {
    const b = byId.get(String(id));
    if (b) {
      resolved.push(b);
      reasons.push(`resident ${shiftCode} backup`);
    }
  });

  // Keep only on-duty from assignment at this stage
  const onDutyAssigned = dedupeById(resolved).filter((s) => isStaffOnDuty(s, now));
  const roleCandidates = byRoles(staffs, rule?.primaryRoles || []);
  const onDutyByRole = roleCandidates.filter((s) => isStaffOnDuty(s, now));

  // Mode-specific behavior
  if (routingMode === "ASSIGNEE_FIRST") {
    if (onDutyAssigned.length > 0) {
      return { recipients: onDutyAssigned, reasons };
    }
    if (onDutyByRole.length > 0) {
      return { recipients: dedupeById(onDutyByRole), reasons: [...reasons, "on-duty role fallback"] };
    }
  }

  if (routingMode === "ON_DUTY_ROLE") {
    if (onDutyByRole.length > 0) {
      return { recipients: dedupeById(onDutyByRole), reasons: ["on-duty role"] };
    }
    if (onDutyAssigned.length > 0) {
      return { recipients: onDutyAssigned, reasons: [...reasons, "resident shift fallback"] };
    }
  }

  if (routingMode === "ROLE_BASED") {
    if (roleCandidates.length > 0) {
      return { recipients: dedupeById(roleCandidates), reasons: ["primary roles"] };
    }
    if (onDutyAssigned.length > 0) {
      return { recipients: onDutyAssigned, reasons: [...reasons, "resident shift fallback"] };
    }
    if (onDutyByRole.length > 0) {
      return { recipients: dedupeById(onDutyByRole), reasons: ["on-duty role fallback"] };
    }
  }

  // 4) Manual fallback from settings (role + staff picks, not on-duty constrained)
  const explicitStaffIds = [
    ...(rule?.primaryStaffIds || []),
    ...(rule?.secondaryStaffIds || []),
  ].map((x) => String(x));
  const explicitStaffs = explicitStaffIds
    .map((id) => byId.get(String(id)))
    .filter(Boolean);
  const secondaryByRole = byRoles(staffs, [
    ...(rule?.primaryRoles || []),
    ...(rule?.secondaryRoles || []),
  ]);
  const finalFallback = dedupeById([...explicitStaffs, ...secondaryByRole]);
  return { recipients: finalFallback, reasons: [...reasons, "policy fallback"] };
}

export function normalizeAlertType(input) {
  const raw = String(input || "").trim().toUpperCase();
  if (!raw) return raw;

  if (raw.includes("MEDICATION") && raw.includes("LATE")) return "MEDICATION_LATE";
  if (raw.includes("MED") && raw.includes("LATE")) return "MEDICATION_LATE";

  if (raw.includes("RESIDENT") && raw.includes("DOC")) return "RESIDENT_DOC_EXPIRED";
  if (raw.includes("RESIDENT") && raw.includes("EXPIRED")) return "RESIDENT_DOC_EXPIRED";

  if (raw.includes("STAFF") && raw.includes("DOC")) return "STAFF_DOC_EXPIRED";
  if (raw.includes("STAFF") && raw.includes("EXPIRED")) return "STAFF_DOC_EXPIRED";

  if (raw.includes("BUSINESS") && raw.includes("DOC")) return "BUSINESS_DOC_EXPIRED";
  if (raw.includes("BUSINESS") && raw.includes("EXPIRED")) return "BUSINESS_DOC_EXPIRED";

  return raw;
}
